/**
 * Integrations Store
 *
 * Versione GraphQL - configurazioni persistite nel database.
 * Lo stato transiente (connection status, eventi webhook) rimane in memoria locale.
 *
 * Zustand store for managing external integrations state.
 * Handles WhatsApp Business, Telegram Bot, and future integrations.
 */

import { create } from 'zustand';
import type { WebhookEvent } from '../services/whatsappService';
import {
  configureTelegram,
  clearTelegramConfig,
  getTelegramBot,
  getTelegramWebhookInfo,
  startPolling,
  stopPolling,
  isPollingActive,
  setPollingInterval,
  getPollingInterval,
  type TelegramBot,
  type TelegramUpdate,
} from '../services/telegramService';
import { graphqlFetch } from '../database/graphqlClient';

// =============================================================================
// GRAPHQL HELPER (uses centralized client)
// =============================================================================

async function graphqlRequest<T>(query: string, variables?: Record<string, unknown>): Promise<T | null> {
  try {
    const response = await graphqlFetch<T>(query, variables);
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      return null;
    }
    return response.data || null;
  } catch (error) {
    console.error('GraphQL request error:', error);
    return null;
  }
}

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  integrationConfig: `
    query GetIntegrationConfig($userId: String) {
      integrationConfig(userId: $userId) {
        id
        userId
        whatsapp {
          configured
          phoneNumberId
          businessAccountId
          businessName
          phoneNumber
        }
        telegram {
          configured
          botToken
          botUsername
          botFirstName
          botId
          pollingInterval
          commands {
            command
            description
          }
        }
        twilio {
          configured
          accountSid
          phoneNumber
          twimlAppSid
          recordingEnabled
          callerId
          timeout
          holdMusicUrl
          lastVerifiedAt
        }
        createdAt
        updatedAt
      }
    }
  `,
};

const MUTATIONS = {
  saveIntegrationConfig: `
    mutation SaveIntegrationConfig($input: IntegrationConfigInput!, $userId: String) {
      saveIntegrationConfig(input: $input, userId: $userId) {
        id
        userId
        whatsapp {
          configured
          phoneNumberId
          businessAccountId
          businessName
          phoneNumber
        }
        telegram {
          configured
          botToken
          botUsername
          botFirstName
          botId
          pollingInterval
          commands {
            command
            description
          }
        }
        twilio {
          configured
          accountSid
          phoneNumber
          twimlAppSid
          recordingEnabled
          callerId
          timeout
          holdMusicUrl
          lastVerifiedAt
        }
        createdAt
        updatedAt
      }
    }
  `,
};

// ============================================================================
// Types and Interfaces
// ============================================================================

/** WhatsApp integration state */
export interface WhatsAppIntegrationState {
  configured: boolean;
  phoneNumberId: string;
  businessAccountId: string;
  businessName: string;
  phoneNumber: string;
  lastWebhookEvent: string;
  webhookEvents: WebhookEvent[];
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  lastError?: string;
}

/** Telegram received message for log display */
export interface TelegramReceivedMessage {
  id: string;
  updateId: number;
  chatId: number;
  chatType: string;
  username?: string;
  firstName?: string;
  text: string;
  timestamp: number;
}

/** Telegram integration state */
export interface TelegramIntegrationState {
  configured: boolean;
  botToken: string;
  botUsername: string;
  botFirstName: string;
  botId: number | null;
  webhookActive: boolean;
  webhookUrl: string;
  pollingActive: boolean;
  pollingInterval: number;
  commands: Array<{ command: string; description: string }>;
  recentMessages: TelegramReceivedMessage[];
  lastError: string | null;
}

/** Twilio VoIP integration state */
export interface TwilioIntegrationState {
  configured: boolean;
  accountSid: string;
  phoneNumber: string;
  twimlAppSid: string;
  recordingEnabled: boolean;
  callerId: string;
  timeout: number;
  holdMusicUrl: string;
  lastVerifiedAt?: string;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  lastError?: string;
}

/** All integrations state */
export interface IntegrationsState {
  whatsapp: WhatsAppIntegrationState;
  telegram: TelegramIntegrationState;
  twilio: TwilioIntegrationState;
  initialized: boolean;
}

/** Store actions */
export interface IntegrationsActions {
  // Load data
  loadData: () => Promise<void>;
  saveConfig: () => Promise<void>;

  // WhatsApp actions
  setWhatsAppConfigured: (data: {
    phoneNumberId: string;
    businessAccountId: string;
    businessName: string;
    phoneNumber: string;
  }) => void;
  setWhatsAppDisconnected: () => void;
  setWhatsAppConnectionStatus: (status: WhatsAppIntegrationState['connectionStatus']) => void;
  setWhatsAppError: (error: string) => void;
  addWhatsAppWebhookEvent: (event: WebhookEvent) => void;
  clearWhatsAppWebhookEvents: () => void;

  // Telegram actions
  configureTelegramBot: (token: string) => Promise<TelegramBot>;
  disconnectTelegramBot: () => void;
  setTelegramWebhookActive: (active: boolean, url?: string) => void;
  startTelegramPolling: (interval?: number) => void;
  stopTelegramPolling: () => void;
  setTelegramPollingInterval: (interval: number) => void;
  setTelegramCommands: (commands: Array<{ command: string; description: string }>) => void;
  addTelegramReceivedMessage: (update: TelegramUpdate) => void;
  clearTelegramReceivedMessages: () => void;
  setTelegramError: (error: string | null) => void;
  refreshTelegramStatus: () => Promise<void>;

  // Twilio VoIP actions
  configureTwilio: (config: Partial<TwilioIntegrationState>) => void;
  updateTwilioSettings: (settings: Partial<TwilioIntegrationState>) => void;
  setTwilioVerified: () => void;
  clearTwilio: () => void;
  setTwilioConnectionStatus: (status: TwilioIntegrationState['connectionStatus']) => void;
  setTwilioError: (error: string) => void;

  // General actions
  resetAllIntegrations: () => void;
}

/** Complete store type */
export type IntegrationsStoreState = IntegrationsState & IntegrationsActions;

// ============================================================================
// Initial State
// ============================================================================

const initialWhatsAppState: WhatsAppIntegrationState = {
  configured: false,
  phoneNumberId: '',
  businessAccountId: '',
  businessName: '',
  phoneNumber: '',
  lastWebhookEvent: '',
  webhookEvents: [],
  connectionStatus: 'disconnected',
  lastError: undefined,
};

const initialTelegramState: TelegramIntegrationState = {
  configured: false,
  botToken: '',
  botUsername: '',
  botFirstName: '',
  botId: null,
  webhookActive: false,
  webhookUrl: '',
  pollingActive: false,
  pollingInterval: 5000,
  commands: [],
  recentMessages: [],
  lastError: null,
};

const initialTwilioState: TwilioIntegrationState = {
  configured: false,
  accountSid: '',
  phoneNumber: '',
  twimlAppSid: '',
  recordingEnabled: false,
  callerId: '',
  timeout: 30,
  holdMusicUrl: '',
  lastVerifiedAt: undefined,
  connectionStatus: 'disconnected',
  lastError: undefined,
};

const initialState: IntegrationsState = {
  whatsapp: initialWhatsAppState,
  telegram: initialTelegramState,
  twilio: initialTwilioState,
  initialized: false,
};

// ============================================================================
// Helper Functions
// ============================================================================

function configToInput(state: IntegrationsState) {
  return {
    whatsapp: {
      configured: state.whatsapp.configured,
      phone_number_id: state.whatsapp.phoneNumberId,
      business_account_id: state.whatsapp.businessAccountId,
      business_name: state.whatsapp.businessName,
      phone_number: state.whatsapp.phoneNumber,
    },
    telegram: {
      configured: state.telegram.configured,
      bot_token: state.telegram.botToken,
      bot_username: state.telegram.botUsername,
      bot_first_name: state.telegram.botFirstName,
      bot_id: state.telegram.botId ? Number(state.telegram.botId) : null,
      polling_interval: state.telegram.pollingInterval,
      commands: state.telegram.commands.map((c) => ({
        command: c.command,
        description: c.description,
      })),
    },
    twilio: {
      configured: state.twilio.configured,
      account_sid: state.twilio.accountSid,
      phone_number: state.twilio.phoneNumber,
      twiml_app_sid: state.twilio.twimlAppSid,
      recording_enabled: state.twilio.recordingEnabled,
      caller_id: state.twilio.callerId,
      timeout: state.twilio.timeout,
      hold_music_url: state.twilio.holdMusicUrl,
      last_verified_at: state.twilio.lastVerifiedAt || null,
    },
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useIntegrationsStore = create<IntegrationsStoreState>()((set, get) => ({
  // Initial state
  ...initialState,

  // Load data from GraphQL
  loadData: async () => {
    const data = await graphqlRequest<{ integrationConfig: any }>(
      QUERIES.integrationConfig,
      { userId: null }
    );

    if (data?.integrationConfig) {
      const c = data.integrationConfig;

      // Update state from DB config
      set({
        whatsapp: {
          ...initialWhatsAppState,
          configured: c.whatsapp.configured,
          phoneNumberId: c.whatsapp.phoneNumberId,
          businessAccountId: c.whatsapp.businessAccountId,
          businessName: c.whatsapp.businessName,
          phoneNumber: c.whatsapp.phoneNumber,
          connectionStatus: c.whatsapp.configured ? 'connected' : 'disconnected',
        },
        telegram: {
          ...initialTelegramState,
          configured: c.telegram.configured,
          botToken: c.telegram.botToken,
          botUsername: c.telegram.botUsername,
          botFirstName: c.telegram.botFirstName,
          botId: c.telegram.botId,
          pollingInterval: c.telegram.pollingInterval,
          commands: c.telegram.commands || [],
        },
        twilio: {
          ...initialTwilioState,
          configured: c.twilio.configured,
          accountSid: c.twilio.accountSid,
          phoneNumber: c.twilio.phoneNumber,
          twimlAppSid: c.twilio.twimlAppSid,
          recordingEnabled: c.twilio.recordingEnabled,
          callerId: c.twilio.callerId,
          timeout: c.twilio.timeout,
          holdMusicUrl: c.twilio.holdMusicUrl,
          lastVerifiedAt: c.twilio.lastVerifiedAt || undefined,
          connectionStatus: c.twilio.configured ? 'connected' : 'disconnected',
        },
        initialized: true,
      });

      // Re-configure Telegram service if configured
      if (c.telegram.configured && c.telegram.botToken) {
        configureTelegram({ botToken: c.telegram.botToken });
      }
    } else {
      set({ initialized: true });
    }
  },

  // Save config to GraphQL
  saveConfig: async () => {
    const state = get();
    const input = configToInput(state);

    await graphqlRequest(MUTATIONS.saveIntegrationConfig, { input, userId: null });
  },

  // ========================================================================
  // WhatsApp Actions
  // ========================================================================

  setWhatsAppConfigured: (data) => {
    set({
      whatsapp: {
        ...get().whatsapp,
        configured: true,
        phoneNumberId: data.phoneNumberId,
        businessAccountId: data.businessAccountId,
        businessName: data.businessName,
        phoneNumber: data.phoneNumber,
        connectionStatus: 'connected',
        lastError: undefined,
      },
    });
    get().saveConfig();
  },

  setWhatsAppDisconnected: () => {
    set({
      whatsapp: {
        ...initialWhatsAppState,
      },
    });
    get().saveConfig();
  },

  setWhatsAppConnectionStatus: (status) => {
    set({
      whatsapp: {
        ...get().whatsapp,
        connectionStatus: status,
        lastError: status === 'connected' ? undefined : get().whatsapp.lastError,
      },
    });
  },

  setWhatsAppError: (error) => {
    set({
      whatsapp: {
        ...get().whatsapp,
        connectionStatus: 'error',
        lastError: error,
      },
    });
  },

  addWhatsAppWebhookEvent: (event) => {
    const currentEvents = get().whatsapp.webhookEvents;
    const updatedEvents = [event, ...currentEvents].slice(0, 50);

    set({
      whatsapp: {
        ...get().whatsapp,
        lastWebhookEvent: event.timestamp,
        webhookEvents: updatedEvents,
      },
    });
  },

  clearWhatsAppWebhookEvents: () => {
    set({
      whatsapp: {
        ...get().whatsapp,
        webhookEvents: [],
        lastWebhookEvent: '',
      },
    });
  },

  // ========================================================================
  // Telegram Actions
  // ========================================================================

  configureTelegramBot: async (token: string) => {
    try {
      configureTelegram({ botToken: token });
      const bot = await getTelegramBot();

      set({
        telegram: {
          ...get().telegram,
          configured: true,
          botToken: token,
          botUsername: bot.username,
          botFirstName: bot.firstName,
          botId: bot.id,
          lastError: null,
        },
      });

      await get().saveConfig();
      return bot;
    } catch (error) {
      clearTelegramConfig();

      const errorMessage = error instanceof Error ? error.message : 'Failed to configure Telegram bot';

      set({
        telegram: {
          ...initialTelegramState,
          lastError: errorMessage,
        },
      });

      throw error;
    }
  },

  disconnectTelegramBot: () => {
    stopPolling();
    clearTelegramConfig();

    set({
      telegram: initialTelegramState,
    });

    get().saveConfig();
  },

  setTelegramWebhookActive: (active: boolean, url?: string) => {
    set({
      telegram: {
        ...get().telegram,
        webhookActive: active,
        webhookUrl: url || get().telegram.webhookUrl,
        pollingActive: active ? false : get().telegram.pollingActive,
      },
    });

    if (active && isPollingActive()) {
      stopPolling();
    }
  },

  startTelegramPolling: (interval?: number) => {
    const currentInterval = interval || get().telegram.pollingInterval;

    set({
      telegram: {
        ...get().telegram,
        webhookActive: false,
        pollingActive: true,
        pollingInterval: currentInterval,
      },
    });

    setPollingInterval(currentInterval);
    startPolling(currentInterval);
  },

  stopTelegramPolling: () => {
    stopPolling();

    set({
      telegram: {
        ...get().telegram,
        pollingActive: false,
      },
    });
  },

  setTelegramPollingInterval: (interval: number) => {
    set({
      telegram: {
        ...get().telegram,
        pollingInterval: interval,
      },
    });

    setPollingInterval(interval);

    if (get().telegram.pollingActive) {
      stopPolling();
      startPolling(interval);
    }

    get().saveConfig();
  },

  setTelegramCommands: (commands) => {
    set({
      telegram: {
        ...get().telegram,
        commands,
      },
    });

    get().saveConfig();
  },

  addTelegramReceivedMessage: (update: TelegramUpdate) => {
    const message = update.message || update.editedMessage || update.channelPost;
    if (!message) return;

    const receivedMessage: TelegramReceivedMessage = {
      id: `${update.updateId}-${message.messageId}`,
      updateId: update.updateId,
      chatId: message.chat.id,
      chatType: message.chat.type,
      username: message.from?.username,
      firstName: message.from?.firstName,
      text: message.text || message.caption || '[Media]',
      timestamp: message.date * 1000,
    };

    set({
      telegram: {
        ...get().telegram,
        recentMessages: [
          receivedMessage,
          ...get().telegram.recentMessages.slice(0, 49),
        ],
      },
    });
  },

  clearTelegramReceivedMessages: () => {
    set({
      telegram: {
        ...get().telegram,
        recentMessages: [],
      },
    });
  },

  setTelegramError: (error) => {
    set({
      telegram: {
        ...get().telegram,
        lastError: error,
      },
    });
  },

  refreshTelegramStatus: async () => {
    const { telegram } = get();
    if (!telegram.configured || !telegram.botToken) return;

    try {
      configureTelegram({ botToken: telegram.botToken });
      const bot = await getTelegramBot();
      const webhookInfo = await getTelegramWebhookInfo();

      set({
        telegram: {
          ...get().telegram,
          botUsername: bot.username,
          botFirstName: bot.firstName,
          botId: bot.id,
          webhookActive: webhookInfo.url !== '',
          webhookUrl: webhookInfo.url,
          pollingActive: isPollingActive(),
          pollingInterval: getPollingInterval(),
          lastError: null,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh status';
      set({
        telegram: {
          ...get().telegram,
          lastError: errorMessage,
        },
      });
    }
  },

  // ========================================================================
  // Twilio VoIP Actions
  // ========================================================================

  configureTwilio: (config) => {
    set({
      twilio: {
        ...get().twilio,
        ...config,
        configured: Boolean(config.accountSid && config.phoneNumber),
        callerId: config.callerId || config.phoneNumber || get().twilio.callerId,
        connectionStatus: Boolean(config.accountSid && config.phoneNumber) ? 'connected' : 'disconnected',
      },
    });

    get().saveConfig();
  },

  updateTwilioSettings: (settings) => {
    set({
      twilio: {
        ...get().twilio,
        ...settings,
      },
    });

    get().saveConfig();
  },

  setTwilioVerified: () => {
    set({
      twilio: {
        ...get().twilio,
        lastVerifiedAt: new Date().toISOString(),
        connectionStatus: 'connected',
        lastError: undefined,
      },
    });

    get().saveConfig();
  },

  clearTwilio: () => {
    set({
      twilio: initialTwilioState,
    });

    get().saveConfig();
  },

  setTwilioConnectionStatus: (status) => {
    set({
      twilio: {
        ...get().twilio,
        connectionStatus: status,
        lastError: status === 'connected' ? undefined : get().twilio.lastError,
      },
    });
  },

  setTwilioError: (error) => {
    set({
      twilio: {
        ...get().twilio,
        connectionStatus: 'error',
        lastError: error,
      },
    });
  },

  // ========================================================================
  // General Actions
  // ========================================================================

  resetAllIntegrations: () => {
    stopPolling();
    clearTelegramConfig();
    set(initialState);
    get().saveConfig();
  },
}));

// ============================================================================
// Selectors
// ============================================================================

// WhatsApp selectors
export const selectWhatsAppState = (state: IntegrationsStoreState) => state.whatsapp;
export const selectIsWhatsAppConfigured = (state: IntegrationsStoreState) => state.whatsapp.configured;
export const selectWhatsAppConnectionStatus = (state: IntegrationsStoreState) => state.whatsapp.connectionStatus;
export const selectWhatsAppBusinessName = (state: IntegrationsStoreState) => state.whatsapp.businessName;
export const selectWhatsAppPhoneNumber = (state: IntegrationsStoreState) => state.whatsapp.phoneNumber;
export const selectWhatsAppWebhookEvents = (state: IntegrationsStoreState) => state.whatsapp.webhookEvents;

// Telegram selectors
export const selectTelegramState = (state: IntegrationsStoreState) => state.telegram;
export const selectIsTelegramConfigured = (state: IntegrationsStoreState) => state.telegram.configured;
export const selectTelegramBotUsername = (state: IntegrationsStoreState) => state.telegram.botUsername;
export const selectTelegramPollingActive = (state: IntegrationsStoreState) => state.telegram.pollingActive;
export const selectTelegramWebhookActive = (state: IntegrationsStoreState) => state.telegram.webhookActive;
export const selectTelegramRecentMessages = (state: IntegrationsStoreState) => state.telegram.recentMessages;
export const selectTelegramCommands = (state: IntegrationsStoreState) => state.telegram.commands;

// Twilio VoIP selectors
export const selectTwilioState = (state: IntegrationsStoreState) => state.twilio;
export const selectIsTwilioConfigured = (state: IntegrationsStoreState) => state.twilio.configured;
export const selectTwilioConnectionStatus = (state: IntegrationsStoreState) => state.twilio.connectionStatus;
export const selectTwilioPhoneNumber = (state: IntegrationsStoreState) => state.twilio.phoneNumber;
export const selectTwilioRecordingEnabled = (state: IntegrationsStoreState) => state.twilio.recordingEnabled;
export const selectTwilioCallerId = (state: IntegrationsStoreState) => state.twilio.callerId;

// ============================================================================
// Helper Hooks
// ============================================================================

// WhatsApp hooks
export function useWhatsAppConfigured(): boolean {
  return useIntegrationsStore((state) => state.whatsapp.configured);
}

export function useWhatsAppConnectionStatus(): WhatsAppIntegrationState['connectionStatus'] {
  return useIntegrationsStore((state) => state.whatsapp.connectionStatus);
}

export function useWhatsAppBusinessInfo(): { name: string; phone: string } {
  return useIntegrationsStore((state) => ({
    name: state.whatsapp.businessName,
    phone: state.whatsapp.phoneNumber,
  }));
}

export function useWhatsAppWebhookEvents(): WebhookEvent[] {
  return useIntegrationsStore((state) => state.whatsapp.webhookEvents);
}

// Telegram hooks
export function useTelegramIntegration(): TelegramIntegrationState {
  return useIntegrationsStore((state) => state.telegram);
}

export function useTelegramConfigured(): boolean {
  return useIntegrationsStore((state) => state.telegram.configured);
}

export function useTelegramBotInfo(): { username: string; firstName: string; id: number | null } {
  return useIntegrationsStore((state) => ({
    username: state.telegram.botUsername,
    firstName: state.telegram.botFirstName,
    id: state.telegram.botId,
  }));
}

export function useTelegramRecentMessages(): TelegramReceivedMessage[] {
  return useIntegrationsStore((state) => state.telegram.recentMessages);
}

// Twilio VoIP hooks
export function useTwilioIntegration(): TwilioIntegrationState {
  return useIntegrationsStore((state) => state.twilio);
}

export function useTwilioConfigured(): boolean {
  return useIntegrationsStore((state) => state.twilio.configured);
}

export function useTwilioConnectionStatus(): TwilioIntegrationState['connectionStatus'] {
  return useIntegrationsStore((state) => state.twilio.connectionStatus);
}

export function useTwilioPhoneInfo(): { phoneNumber: string; callerId: string } {
  return useIntegrationsStore((state) => ({
    phoneNumber: state.twilio.phoneNumber,
    callerId: state.twilio.callerId,
  }));
}

export function useTwilioCallSettings(): {
  recordingEnabled: boolean;
  timeout: number;
  holdMusicUrl: string;
} {
  return useIntegrationsStore((state) => ({
    recordingEnabled: state.twilio.recordingEnabled,
    timeout: state.twilio.timeout,
    holdMusicUrl: state.twilio.holdMusicUrl,
  }));
}

export default useIntegrationsStore;

// Initialize - load data from GraphQL on first access
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useIntegrationsStore.getState().loadData();
  }, 100);
}
